import { Link } from "react-router-dom";
import { HeartHandshake, MessageCircle, Presentation, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface ServicesProps {
  onBookConversation: () => void;
  onRequestLecture: () => void;
}

const offeringKeys = ["item1", "item2", "item3", "item4"] as const;
const notOfferedKeys = ["notOffered1", "notOffered2", "notOffered3", "notOffered4"] as const;

const Services = ({ onBookConversation, onRequestLecture }: ServicesProps) => {
  const { t } = useTranslation();
  const privateParagraphs = t("services.private.description").split("\n\n");
  const lectureParagraphs = t("services.lectures.description").split("\n");

  return (
    <section id="services" className="bg-[#F7FBFA] pt-6 pb-12 sm:pt-8 sm:pb-16">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-8 sm:space-y-12">
          <div className="rounded-[18px] border border-[#006B5F]/15 bg-white p-5 shadow-sm sm:rounded-[20px] sm:p-8">
            <div className="flex items-center gap-3 text-[#006B5F]">
              <div className="rounded-xl bg-[#006B5F]/10 p-2">
                <MessageCircle className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-semibold sm:text-3xl">{t("services.private.title")}</h2>
            </div>

            <div className="mt-5 max-w-4xl space-y-4 text-base leading-7 text-[#355852] sm:space-y-5 sm:text-lg sm:leading-8">
              {privateParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph.trim()}</p>
              ))}
            </div>

            <div className="mt-6 border-t border-[#006B5F]/12 pt-6">
              <div className="flex max-w-4xl flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex items-center gap-3 text-[#006B5F] sm:w-[260px] sm:shrink-0">
                  <div className="rounded-xl bg-[#F2F8F6] p-2">
                    <HeartHandshake className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold leading-tight sm:text-xl">
                    {t("services.private.divorceSupport.title")}
                  </h3>
                </div>

                <div className="space-y-3 text-base leading-7 text-[#355852]">
                  <p>{t("services.private.divorceSupport.description")}</p>
                  <p>{t("services.private.divorceSupport.boundary")}</p>
                  <p className="font-medium italic text-[#3E7B72]">
                    {t("services.private.divorceSupport.hope")}
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={onBookConversation}
              className="mt-6 w-full bg-[#3E7B72] px-8 text-white hover:bg-[#32655e] sm:w-auto"
            >
              {t("services.private.book")}
            </Button>
          </div>

          <div className="rounded-[18px] border border-[#006B5F]/15 bg-[#EEF7F5] p-5 shadow-sm sm:rounded-[20px] sm:p-8">
            <h2 className="text-2xl font-semibold text-[#006B5F] sm:text-3xl">
              {t("services.note.title")}
            </h2>
            <p className="mt-4 max-w-4xl text-base leading-7 text-[#355852] sm:text-lg sm:leading-8">
              {t("services.about.clarityText")}
            </p>
            <p className="mt-6 text-xl font-semibold text-[#355852] sm:mt-8 sm:text-2xl">
              {t("services.about.disclaimer")}
            </p>

            <ul className="mt-4 space-y-3 sm:space-y-4">
              {notOfferedKeys.map((key) => (
                <li key={key} className="flex items-start gap-3 sm:gap-4 text-base leading-7 text-[#355852] sm:text-lg sm:leading-8">
                  <span className="mt-3 h-2 w-2 rounded-full bg-[#3E7B72]" />
                  <span>{t(`services.about.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-[#006B5F] sm:text-3xl">
                {t("services.therapists.title")}
              </h2>
              <p className="mt-3 text-base leading-7 text-[#4E6764] sm:text-lg">
                {t("services.therapists.subtitle")}
              </p>
            </div>

            <div className="mt-6 grid gap-6 sm:mt-8 sm:gap-8 lg:grid-cols-2">
              <div className="rounded-[18px] border border-[#006B5F]/15 bg-white p-5 shadow-sm sm:rounded-[20px] sm:p-8">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                  <img
                    src="/images/JK.jpeg"
                    alt="Josef Kristensen"
                    className="h-24 w-24 rounded-full border-4 border-[#3E7B72] object-cover"
                  />
                  <div>
                    <h3 className="text-2xl font-semibold text-[#006B5F] sm:text-3xl">Josef Kristensen</h3>
                    <p className="mt-1 text-base text-[#4E6764] sm:text-lg">
                      {t("services.therapists.josef.title")}
                    </p>
                  </div>
                </div>

                <p className="mt-5 text-base leading-7 text-[#355852] sm:mt-6 sm:text-lg sm:leading-8">
                  {t("services.therapists.josef.bio")}
                </p>

                <Link
                  to="/therapists"
                  className="mt-6 inline-flex text-base font-medium text-[#006B5F] underline underline-offset-4"
                >
                  {t("services.therapists.josef.readMore")}
                </Link>
              </div>

              <div className="flex min-h-[240px] flex-col items-center justify-center rounded-[18px] border border-[#006B5F]/15 bg-[#EEF7F5] p-5 text-center shadow-sm sm:min-h-[280px] sm:rounded-[20px] sm:p-8">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#DDEEEA]">
                  <User className="h-10 w-10 text-[#6A8F88]" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-[#3E7B72] sm:mt-6 sm:text-3xl">
                  {t("services.therapists.coming.title")}
                </h3>
                <p className="mt-4 max-w-md text-base leading-7 text-[#5B7A74] sm:text-lg sm:leading-8">
                  {t("services.therapists.coming.description")}
                </p>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="text-center">
              <p className="text-base leading-7 text-[#4E6764] sm:text-lg">{t("about.subtitle")}</p>
            </div>

            <div className="mt-8 space-y-6 sm:mt-10 sm:space-y-8">
              <div className="border-t border-[#006B5F]/12 pt-6 sm:pt-8">
                <h3 className="text-2xl font-semibold text-[#006B5F] sm:text-3xl">
                  {t("about.whatIs.title")}
                </h3>
                <p className="mt-4 text-base leading-7 text-[#355852] sm:text-lg sm:leading-8">
                  {t("about.description")}
                </p>
              </div>

              <div className="border-t border-[#006B5F]/12 pt-6 sm:pt-8">
                <h3 className="text-2xl font-semibold text-[#006B5F] sm:text-3xl">
                  {t("about.background.title")}
                </h3>
                <p className="mt-4 text-base leading-7 text-[#355852] sm:text-lg sm:leading-8">
                  {t("about.development")}
                </p>
              </div>
            </div>

            <div className="mt-8 rounded-[18px] border border-[#006B5F]/15 bg-white p-5 shadow-sm sm:mt-10 sm:rounded-[20px] sm:p-8">
              <h3 className="text-center text-2xl font-semibold text-[#3E7B72] sm:text-4xl">
                {t("services.offeringsTitle")}
              </h3>

              <div className="mt-6 sm:mt-8">
                <h4 className="text-xl font-semibold text-[#006B5F] sm:text-2xl">
                  {t("services.about.offerings")}
                </h4>
                <ul className="mt-4 space-y-3 sm:space-y-4">
                  {offeringKeys.map((key) => (
                    <li key={key} className="flex items-start gap-3 sm:gap-4 text-base leading-7 text-[#355852] sm:text-lg sm:leading-8">
                      <span className="mt-3 h-2 w-2 rounded-full bg-[#006B5F]" />
                      <span>{t(`services.about.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-2xl font-semibold text-[#006B5F] sm:text-4xl">
              {t("services.lectures.heading")}
            </h2>

            <div className="mt-6 rounded-[18px] border border-[#006B5F]/15 bg-white p-5 shadow-sm sm:mt-8 sm:rounded-[20px] sm:p-8">
              <div className="flex items-center gap-3 text-[#006B5F]">
                <div className="rounded-xl bg-[#006B5F]/10 p-2">
                  <Presentation className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold sm:text-2xl">{t("services.lectures.title")}</h3>
              </div>

              <div className="mt-5 space-y-4 text-base leading-7 text-[#355852] sm:mt-6 sm:space-y-5 sm:text-lg sm:leading-8">
                {lectureParagraphs.map((paragraph) => (
                  paragraph.trim() ? <p key={paragraph}>{paragraph.trim()}</p> : null
                ))}
              </div>

              <Button
                onClick={onRequestLecture}
                className="mt-6 w-full bg-[#3E7B72] px-8 text-white hover:bg-[#32655e] sm:mt-8 sm:w-auto"
              >
                {t("services.lectures.request")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
